@file:Suppress("unused")
package com.dignicate.web.css

import org.jetbrains.compose.web.css.CSSAngleValue
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.CSSLengthValue
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.px
import kotlin.collections.plusAssign
import kotlin.text.Regex

/**
 * Type-safe helper for CSS z-index.
 *
 * CSS spec allows either an integer or the keyword `auto`.
 * We provide overloads for Int and the keyword for clarity.
 */
fun StyleScope.zIndex(value: Int) { property("z-index", value.toString()) }

/** Keyword form of z-index ("auto"). */
fun StyleScope.zIndexAuto() { property("z-index", "auto") }

// --- Theme CSS Variable enum -------------------------------------------------
/**
 * Enumerates theme CSS custom properties without leading `--`.
 * Use helpers like define(ThemeVar.Bg, "#fff") or backgroundVar(ThemeVar.Bg).
 */
enum class ThemeVar(val id: String) {
    Bg("bg"),
    Fg("fg"),
    Muted("muted"),
    CardBg("card-bg"),
    CardBorder("card-border"),
    Shadow1("shadow-1"),
    Shadow1Hover("shadow-1-hover"),
    HeaderHeight("header-height"),
    FabBg("fab-bg"),
    FabFg("fab-fg"),
    FabBorder("fab-border"),
    FabShadow("fab-shadow"),
    FabShadowHover("fab-shadow-hover")
}

private val HEX_RE = Regex("^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$")

// Define raw value for any variable
fun StyleScope.define(varName: ThemeVar, value: String) {
    property("--${varName.id}", value)
}

// Define hex color (#rgb or #rrggbb)
fun StyleScope.defineColor(varName: ThemeVar, hex: String) {
    require(HEX_RE.matches(hex)) { "Invalid hex color: $hex" }
    define(varName, hex)
}

// Define rgba color with validation
fun StyleScope.defineRgba(varName: ThemeVar, r: Int, g: Int, b: Int, a: Double) {
    require(r in 0..255 && g in 0..255 && b in 0..255) { "RGB components must be 0..255" }
    require(a in 0.0..1.0) { "Alpha must be 0.0..1.0" }
    define(varName, "rgba($r,$g,$b,$a)")
}

// Overloads for using variables in styles (value side) -----------------------
sealed class ColorValue {
    data class Var(val value: ThemeVar) : ColorValue()
    object Inherit : ColorValue()
    data class Rgba(val r: Int, val g: Int, val b: Int, val a: Double) : ColorValue()
}

fun StyleScope.color(value: ColorValue) {
    val colorValue = when (value) {
        is ColorValue.Var -> "var(--${value.value.id})"
        is ColorValue.Inherit -> "inherit"
        is ColorValue.Rgba -> "rgba(${value.r},${value.g},${value.b},${value.a})"
    }
    property("color", colorValue)
}

fun StyleScope.backgroundColor(value: CSSColorValue) {
    property("background-color", value.toString())
}
fun StyleScope.backgroundColor(varName: ThemeVar) {
    property("background-color", "var(--${varName.id})")
}
fun StyleScope.background(value: CSSColorValue) {
    property("background", value.toString())
}
fun StyleScope.background(varName: ThemeVar) {
    property("background", "var(--${varName.id})")
}
fun StyleScope.background(value: String) {
    property("background", value)
}
fun StyleScope.boxShadowVar(varName: ThemeVar) { boxShadow("var(--${varName.id})") }
fun StyleScope.borderColorVar(varName: ThemeVar) { property("border-color", "var(--${varName.id})") }
fun StyleScope.borderVar(width: CSSLengthValue, style: BorderStyle, varName: ThemeVar) {
    property("border", "$width ${style.css} var(--${varName.id})")
}

// Type-safe backdrop-filter DSL
class BackdropFilterDsl internal constructor() {
    private val parts = mutableListOf<String>()
    /** blur(8px) */
    fun blur(radius: CSSLengthValue) {
        parts += "blur($radius)"
    }
    /** saturate(180%) */
    fun saturate(percent: Int) {
        require(percent >= 0) { "Saturate percent must be >= 0" }
        parts += "saturate(${percent}%)"
    }
    /** saturate(1.8) factor form (1.0 == 100%) */
    fun saturateFactor(factor: Double) {
        require(factor >= 0.0) { "Saturate factor must be >= 0" }
        parts += "saturate($factor)"
    }
    internal fun build(): String = parts.joinToString(" ")
}

fun StyleScope.backdropFilter(block: BackdropFilterDsl.() -> Unit) {
    val dsl = BackdropFilterDsl().apply(block)
    property("backdrop-filter", dsl.build())
}

// --- box-shadow DSL ---------------------------------------------------------
/**
 * Represents a single layer within a CSS box-shadow.
 * A full box-shadow can consist of multiple layers, separated by commas.
 */
data class BoxShadow(
    val offsetX: CSSLengthValue,
    val offsetY: CSSLengthValue,
    val blurRadius: CSSLengthValue? = null,
    val spreadRadius: CSSLengthValue? = null,
    val color: CSSColorValue? = null,
    val inset: Boolean = false,
) {
    fun toCss(): String = buildString {
        if (inset) append("inset ")
        append(offsetX).append(' ').append(offsetY)
        blurRadius?.let { append(' ').append(it) }
        spreadRadius?.let { append(' ').append(it) }
        color?.let { append(' ').append(colorToString(it)) }
    }
    private fun colorToString(c: CSSColorValue): String = c.toString()
}

class BoxShadowDsl internal constructor() {
    private val list = mutableListOf<BoxShadow>()
    /** Add a shadow. Leave blur/spread null if not needed. */
    fun shadow(
        offsetX: CSSLengthValue,
        offsetY: CSSLengthValue,
        blurRadius: CSSLengthValue? = null,
        spreadRadius: CSSLengthValue? = null,
        color: CSSColorValue? = null,
        inset: Boolean = false,
    ) {
        list += BoxShadow(
            offsetX,
            offsetY,
            blurRadius,
            spreadRadius,
            color,
            inset
        )
    }
    internal fun build(): String = list.joinToString(", ") { it.toCss() }
    internal fun isEmpty() = list.isEmpty()
}

/**
 * Builds a `box-shadow` style using a type-safe DSL.
 * This DSL is useful for creating complex shadows or multiple shadow layers.
 * If no shadows are added in the block, it defaults to `box-shadow: none`.
 *
 * Example for multiple shadows:
 * ```
 * boxShadow {
 *     shadow(0.px, 2.px, 4.px, color = rgba(0,0,0,0.1))
 *     shadow(0.px, 5.px, 10.px, color = rgba(0,0,0,0.05), inset = true)
 * }
 * ```
 */
fun StyleScope.boxShadow(block: BoxShadowDsl.() -> Unit) {
    val dsl = BoxShadowDsl().apply(block)
    if (dsl.isEmpty()) boxShadow("none") else boxShadow(dsl.build())
}

/** Convenience overload for creating a single box-shadow. */
fun StyleScope.boxShadow(
    offsetX: CSSLengthValue,
    offsetY: CSSLengthValue,
    blurRadius: CSSLengthValue? = null,
    spreadRadius: CSSLengthValue? = null,
    color: CSSColorValue? = null,
    inset: Boolean = false,
) {
    boxShadow {
        shadow(offsetX, offsetY, blurRadius, spreadRadius, color, inset)
    }
}

/** Convenience for a single shadow without DSL. */
fun StyleScope.boxShadow(shadow: BoxShadow) { boxShadow(shadow.toCss()) }

/** Sets 'box-shadow: none'. */
fun StyleScope.boxShadowNone() { boxShadow("none") }

/** Simple rgba color helper returning CSSColorValue-like string (Compose Color lacks alpha builder). */
fun rgbaColor(r: Int, g: Int, b: Int, a: Double): CSSColorValue {
    require(r in 0..255 && g in 0..255 && b in 0..255) { "RGB components must be 0..255" }
    require(a in 0.0..1.0) { "Alpha must be 0.0..1.0" }
    // Use Color.rgba once Compose adds it; for now fallback to string wrapper
    return object : CSSColorValue { override fun toString() = "rgba($r,$g,$b,$a)" }
}

// --- transition DSL ---------------------------------------------------------
enum class TransitionProperty(val propertyName: String) {
    OPACITY("opacity"),
    TRANSFORM("transform"),
    COLOR("color"),
    BACKGROUND_COLOR("background-color"),
    BOX_SHADOW("box-shadow"),
    BORDER_COLOR("border-color"),
    ALL("all")
}

sealed class CssTime(private val repr: String) {
    override fun toString(): String = repr
    class Millis(val ms: Int) : CssTime("${ms}ms")
    class Seconds(val sec: Double) : CssTime("${sec}s")
}
fun ms(value: Int): CssTime = CssTime.Millis(value)
fun s(value: Double): CssTime = CssTime.Seconds(value)

enum class TimingFunction(val css: String) {
    Ease("ease"), Linear("linear"), EaseIn("ease-in"), EaseOut("ease-out"), EaseInOut("ease-in-out")
}

class TransitionDsl internal constructor() {
    private data class Item(
        val property: String,
        val duration: CssTime,
        val timing: String?,
        val delay: CssTime?
    ) {
        fun toCss(): String = buildString {
            append(property).append(' ').append(duration)
            timing?.let { append(' ').append(it) }
            delay?.let { append(' ').append(it) }
        }
    }
    private val items = mutableListOf<Item>()

    /** Add a transition. Example: t(TransitionProperty.OPACITY, ms(160), timing = TimingFunction.EaseOut) */
    fun t(
        property: TransitionProperty,
        duration: CssTime,
        timing: TimingFunction? = TimingFunction.Ease,
        delay: CssTime? = null,
        customTiming: String? = null,
    ) {
        items += Item(property.propertyName, duration, customTiming ?: timing?.css, delay)
    }

    internal fun build(): String = items.joinToString(", ") { it.toCss() }
    internal fun isEmpty(): Boolean = items.isEmpty()
}

fun StyleScope.transition(block: TransitionDsl.() -> Unit) {
    val dsl = TransitionDsl().apply(block)
    if (!dsl.isEmpty()) property("transition", dsl.build())
}

/** Type-safe opacity helper, supports 0.0..1.0. */
fun StyleScope.opacity(value: Double) {
    require(value in 0.0..1.0) { "Opacity must be between 0.0 and 1.0" }
    // Format without scientific notation; keep trailing zeros trimmed
    val str = if (value == 1.0 || value == 0.0) value.toInt().toString() else value.toString()
    property("opacity", str)
}

/** Convenience opaque/transparent helpers. */
fun StyleScope.opaque() = opacity(1.0)
fun StyleScope.transparent() = opacity(0.0)

// --- gap helper (grid/flex) --------------------------------------------------
fun StyleScope.gap(value: CSSLengthValue) { property("gap", value.toString()) }

// --- font-weight helpers -----------------------------------------------------
enum class FontWeightValue(val css: String) { Normal("400"), Medium("500"), SemiBold("600"), Bold("700"), ExtraBold("800"), Light("300") }
fun StyleScope.fontWeight(weight: FontWeightValue) = property("font-weight", weight.css)
fun StyleScope.fontWeight(value: Int) {
    require(value in 100..900 && value % 100 == 0) { "Font weight must be 100..900 multiple of 100" }
    property("font-weight", value.toString())
}

// --- text-decoration helpers -------------------------------------------------
fun StyleScope.textDecoration(value: String) { property("text-decoration", value) }
fun StyleScope.textDecorationNone() { textDecoration("none") }
fun StyleScope.textDecorationUnderline() { textDecoration("underline") }

// --- border helpers ----------------------------------------------------------
enum class BorderStyle(val css: String) { Solid("solid"), Dashed("dashed"), Dotted("dotted") }
fun StyleScope.border(width: CSSLengthValue, style: BorderStyle, color: String) {
    property("border", "$width ${style.css} $color")
}
fun StyleScope.borderVar(width: CSSLengthValue, style: BorderStyle, varName: String) {
    require(varName.isNotBlank()) { "Variable name must not be blank" }
    property("border", "$width ${style.css} var(--$varName)")
}
fun StyleScope.borderBottom(width: CSSLengthValue, style: BorderStyle, color: String) {
    property("border-bottom", "$width ${style.css} $color")
}
fun StyleScope.borderTop(width: CSSLengthValue, style: BorderStyle, color: String) {
    property("border-top", "$width ${style.css} $color")
}
/** Type-safe borderTop accepting raw CSS string (e.g., "1px solid rgba(...)") */
fun StyleScope.borderTop(value: String) {
    require(value.isNotBlank()) { "Border value must not be blank" }
    property("border-top", value)
}

/** Type-safe boxShadow accepting raw CSS string (e.g., "0 4px 14px rgba(...)") */
fun StyleScope.boxShadow(value: String) {
    require(value.isNotBlank()) { "Box shadow value must not be blank" }
    property("box-shadow", value)
}

// --- filter helpers ----------------------------------------------------------
/** Type-safe filter accepting raw CSS string (e.g., "blur(5px)", "url(#id)") */
fun StyleScope.filter(value: String) {
    require(value.isNotBlank()) { "Filter value must not be blank" }
    property("filter", value)
}

// --- Additional helpers for AppStyleSheet ------------------------------------

/** margin: 0 auto with optional bottom */
fun StyleScope.marginXAuto(bottom: CSSLengthValue = 0.px) {
    property("margin", "0 auto $bottom")
}

/** box-sizing: border-box */
fun StyleScope.borderBox() { property("box-sizing", "border-box") }

/** grid-template-columns: repeat(auto-fit, minmax(minLength, 1fr)) */
fun StyleScope.gridTemplateAutoFitMinmax(minLength: CSSLengthValue, maxTrack: String = "1fr") {
    property("grid-template-columns", "repeat(auto-fit, minmax($minLength, $maxTrack))")
}

fun StyleScope.gridTemplateColumns(value: String) {
    property("grid-template-columns", value)
}

/** transform: translateY(length) */
fun StyleScope.translateY(length: CSSLengthValue) {
    transform { translateY(length) }
}

/** transform: translateZ(length) */
fun StyleScope.translateZ(length: CSSLengthValue) {
    transform { translateZ(length) }
}

// --- transform DSL ---------------------------------------------------------
sealed class TransformFunction {
    data class TranslateY(val value: CSSLengthValue) : TransformFunction() {
        override fun toString() = "translateY($value)"
    }
    data class TranslateZ(val value: CSSLengthValue) : TransformFunction() {
        override fun toString() = "translateZ($value)"
    }
    data class Rotate(val angle: CSSAngleValue) : TransformFunction() {
        override fun toString() = "rotate($angle)"
    }
}

class TransformDsl {
    internal val functions = mutableListOf<TransformFunction>()

    fun translateY(value: CSSLengthValue) {
        functions.add(TransformFunction.TranslateY(value))
    }
    fun translateZ(value: CSSLengthValue) {
        functions.add(TransformFunction.TranslateZ(value))
    }
    fun rotate(angle: CSSAngleValue) {
        functions.add(TransformFunction.Rotate(angle))
    }
}

fun StyleScope.transform(builder: TransformDsl.() -> Unit) {
    val dsl = TransformDsl().apply(builder)
    property("transform", dsl.functions.joinToString(" "))
}


/** line-height: unitless number */
fun StyleScope.lineHeightUnitless(value: Number) {
    property("line-height", value.toString())
}

/** top: calc(var(--X) / divisor) */
fun StyleScope.topCalcVarDivide(varName: ThemeVar, divisor: Int) {
    require(divisor != 0) { "Divisor must not be zero" }
    property("top", "calc(var(--${varName.id}) / $divisor)")
}

/** right: max(minLength, env(safe-area-inset-right)) */
fun StyleScope.rightSafeAreaMax(minLength: CSSLengthValue) {
    property("right", "max($minLength, env(safe-area-inset-right))")
}

/** z-index: max integer value */
fun StyleScope.zIndexMax() = zIndex(Int.MAX_VALUE)

/** cursor: pointer */
fun StyleScope.cursorPointer() { property("cursor", "pointer") }

enum class Cursor(val value: String) {
    Default("default"),
    Pointer("pointer"),
    NotAllowed("not-allowed"),
    Wait("wait"),
    Text("text"),
    Help("help")
}

fun StyleScope.cursor(value: Cursor) {
    property("cursor", value.value)
}

fun StyleScope.right(value: CSSLengthValue) {
    property("right", value.toString())
}

fun StyleScope.right(value: org.jetbrains.compose.web.css.CSSSizeValue<org.jetbrains.compose.web.css.CSSUnit.percent>) {
    property("right", value.toString())
}

fun StyleScope.top(value: CSSLengthValue) {
    property("top", value.toString())
}

fun StyleScope.top(value: org.jetbrains.compose.web.css.CSSSizeValue<org.jetbrains.compose.web.css.CSSUnit.percent>) {
    property("top", value.toString())
}

enum class PointerEvents(val value: String) {
    None("none"),
    Auto("auto"),
    All("all"),
}

fun StyleScope.pointerEvents(value: PointerEvents) {
    property("pointer-events", value.value)
}

enum class BoxSizing(val value: String) {
    ContentBox("content-box"),
    BorderBox("border-box")
}

fun StyleScope.boxSizing(value: BoxSizing) {
    property("box-sizing", value.value)
}

enum class TextAlign(val value: String) {
    Left("left"),
    Right("right"),
    Center("center"),
    Justify("justify"),
    Start("start"),
    End("end")
}

fun StyleScope.textAlign(value: TextAlign) {
    property("text-align", value.value)
}

sealed class OutlineStyle(val value: String) {
    object None : OutlineStyle("none")
}

fun StyleScope.outline(style: OutlineStyle) {
    property("outline", style.value)
}

sealed class HeightValue(val value: String) {
    object Auto : HeightValue("auto")
    object Inherit : HeightValue("inherit")
}

fun StyleScope.height(value: HeightValue) {
    property("height", value.value)
}

fun StyleScope.height(value: CSSLengthValue) {
    property("height", value.toString())
}

fun StyleScope.height(value: org.jetbrains.compose.web.css.CSSSizeValue<org.jetbrains.compose.web.css.CSSUnit.percent>) {
    property("height", value.toString())
}

sealed class WidthValue(val value: String) {
    object Auto : WidthValue("auto")
    object Inherit : WidthValue("inherit")
}

fun StyleScope.width(value: WidthValue) {
    property("width", value.value)
}

fun StyleScope.width(value: CSSLengthValue) {
    property("width", value.toString())
}

fun StyleScope.width(value: org.jetbrains.compose.web.css.CSSSizeValue<org.jetbrains.compose.web.css.CSSUnit.percent>) {
    property("width", value.toString())
}

enum class Isolation(val value: String) {
    Isolate("isolate"),
    Auto("auto"),
}

fun StyleScope.isolation(value: Isolation) {
    property("isolation", value.value)
}

fun StyleScope.inset(value: CSSLengthValue) {
    property("inset", value.toString())
}

// --- margin helpers ---------------------------------------------------------
fun StyleScope.marginTop(value: CSSLengthValue) { property("margin-top", value.toString()) }
fun StyleScope.marginBottom(value: CSSLengthValue) { property("margin-bottom", value.toString()) }
fun StyleScope.marginLeft(value: CSSLengthValue) { property("margin-left", value.toString()) }
fun StyleScope.marginRight(value: CSSLengthValue) { property("margin-right", value.toString()) }

fun StyleScope.marginTopAuto() { property("margin-top", "auto") }
fun StyleScope.marginBottomAuto() { property("margin-bottom", "auto") }
fun StyleScope.marginLeftAuto() { property("margin-left", "auto") }
fun StyleScope.marginRightAuto() { property("margin-right", "auto") }

fun StyleScope.margin(
    top: CSSLengthValue? = null,
    right: CSSLengthValue? = null,
    bottom: CSSLengthValue? = null,
    left: CSSLengthValue? = null
) {
    top?.let { marginTop(it) }
    right?.let { marginRight(it) }
    bottom?.let { marginBottom(it) }
    left?.let { marginLeft(it) }
}

// --- padding helpers ---------------------------------------------------------
fun StyleScope.paddingTop(value: CSSLengthValue) { property("padding-top", value.toString()) }
fun StyleScope.paddingBottom(value: CSSLengthValue) { property("padding-bottom", value.toString()) }
fun StyleScope.paddingLeft(value: CSSLengthValue) { property("padding-left", value.toString()) }
fun StyleScope.paddingRight(value: CSSLengthValue) { property("padding-right", value.toString()) }

fun StyleScope.padding(
    top: CSSLengthValue? = null,
    right: CSSLengthValue? = null,
    bottom: CSSLengthValue? = null,
    left: CSSLengthValue? = null
) {
    top?.let { paddingTop(it) }
    right?.let { paddingRight(it) }
    bottom?.let { paddingBottom(it) }
    left?.let { paddingLeft(it) }
}

fun StyleScope.padding(vertical: CSSLengthValue, horizontal: CSSLengthValue) {
    property("padding", "$vertical $horizontal")
}

fun StyleScope.padding(all: CSSLengthValue) {
    property("padding", all.toString())
}

enum class WillChange(val value: String) {
    Transform("transform"),
    Opacity("opacity"),
    Filter("filter"),
    Contents("contents"),
    ScrollPosition("scroll-position"),
}

fun StyleScope.willChange(vararg properties: WillChange) {
    property("will-change", properties.joinToString(", ") { it.value })
}

enum class Contain(val value: String) {
    None("none"),
    Strict("strict"),
    Content("content"),
    Size("size"),
    InlineSize("inline-size"),
    Layout("layout"),
    Style("style"),
    Paint("paint")
}

fun StyleScope.contain(vararg properties: Contain) {
    property("contain", properties.joinToString(" ") { it.value })
}

enum class FlexDirection(val value: String) {
    Row("row"),
    RowReverse("row-reverse"),
    Column("column"),
    ColumnReverse("column-reverse")
}

fun StyleScope.flexDirection(value: FlexDirection) {
    property("flex-direction", value.value)
}

enum class JustifyContent(val value: String) {
    FlexStart("flex-start"),
    FlexEnd("flex-end"),
    Center("center"),
    SpaceBetween("space-between"),
    SpaceAround("space-around"),
    SpaceEvenly("space-evenly")
}

fun StyleScope.justifyContent(value: JustifyContent) {
    property("justify-content", value.value)
}

fun StyleScope.transformOrigin(value: String) {
    property("transform-origin", value)
}

fun StyleScope.maxWidth(value: String) {
    property("max-width", value)
}

value class FlexGrow(val value: Int) {
    init {
        require(value >= 0) { "flex-grow must be non-negative" }
    }
    override fun toString() = value.toString()
}

value class FlexShrink(val value: Int) {
    init {
        require(value >= 0) { "flex-shrink must be non-negative" }
    }
    override fun toString() = value.toString()
}

sealed class FlexBasis {
    data class Length(val value: CSSLengthValue) : FlexBasis() {
        override fun toString() = value.toString()
    }
    enum class Keyword(val value: String) {
        Auto("auto"),
        Content("content"),
        MaxContent("max-content"),
        MinContent("min-content"),
        FitContent("fit-content");

        override fun toString() = value
    }
}

fun StyleScope.flex(
    grow: FlexGrow,
    shrink: FlexShrink,
    basis: FlexBasis.Keyword
) {
    property("flex", "$grow $shrink $basis")
}

fun StyleScope.flex(
    grow: FlexGrow,
    shrink: FlexShrink,
    basis: FlexBasis.Length
) {
    property("flex", "$grow $shrink $basis")
}
